/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import clsx from 'clsx'
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline'

interface UiCounterProps {
    value: number | ''
    onChange: (value: number | '') => void
    onIncrement?: () => void
    onDecrement?: () => void
    min?: number
    max?: number
    step?: number
    placeholder?: string
    className?: string
    inputClassName?: string
}

const UiCounter: React.FC<UiCounterProps> = ({
    value,
    onChange,
    onIncrement,
    onDecrement,
    min = 0,
    max,
    step = 1,
    placeholder = '0',
    className,
    inputClassName,
}) => {
    const [localValue, setLocalValue] = React.useState<number | ''>(value ?? '')
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

    React.useEffect(() => {
        setLocalValue(value ?? '')
    }, [value])

    const debounceOnChange = (newValue: number | '') => {
        setLocalValue(newValue)

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
            onChange(newValue)
        }, 300)
    }

    const handleIncrement = () => {
        if (onIncrement) {
            onIncrement()
            return
        }
        const currentVal = localValue === '' ? 0 : localValue
        const nextVal = currentVal + step
        if (max !== undefined && nextVal > max) return
        debounceOnChange(nextVal)
    }

    const handleDecrement = () => {
        if (onDecrement) {
            onDecrement()
            return
        }
        const currentVal = localValue === '' ? 0 : localValue
        const nextVal = currentVal - step
        if (min !== undefined && nextVal < min) return
        debounceOnChange(nextVal)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value
        if (val === '') {
            debounceOnChange('')
            return
        }
        const num = Number(val)
        if (isNaN(num)) return
        if (min !== undefined && num < min) return
        if (max !== undefined && num > max) return
        debounceOnChange(num)
    }

    return (
        <div
            className={clsx(
                'UiCounter flex items-center h-10 w-full rounded-lg border border-gray-300 bg-white dark:border-gray-800 dark:bg-gray-950 transition-colors',
                'focus-within:border-blue-500 transition-shadow shadow-sm',
                className
            )}
        >
            <button
                type="button"
                onClick={handleDecrement}
                disabled={
                    localValue !== '' && min !== undefined && localValue <= min
                }
                className="flex items-center justify-center w-8 h-full text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-200 focus:outline-none disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
                <MinusIcon className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
            <div className="flex-1 h-full border-x border-gray-200 dark:border-gray-800 bg-transparent">
                <input
                    type="number"
                    value={localValue}
                    onChange={handleInputChange}
                    className={clsx(
                        'w-full h-full text-center bg-transparent border-none outline-none focus:border-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                        inputClassName
                    )}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    step="any"
                />
            </div>
            <button
                type="button"
                onClick={handleIncrement}
                disabled={
                    localValue !== '' && max !== undefined && localValue >= max
                }
                className="flex items-center justify-center w-8 h-full text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-200 focus:outline-none disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
                <PlusIcon className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
        </div>
    )
}

export default UiCounter
